// IndustriesAdmin.js
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function IndustriesAdmin() {
  const navigate = useNavigate();
  const admin = isAdmin();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [industries, setIndustries] = useState([]);

  const [createForm, setCreateForm] = useState({
    name: "",
    slug: "",
    description: "",
    parent_industry: "",
    is_active: true,
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    slug: "",
    description: "",
    parent_industry: "",
    is_active: true,
  });

  // Redirect non-admins away
  useEffect(() => {
    if (!admin) {
      navigate("/");
    }
  }, [admin, navigate]);

  async function fetchIndustries() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setIndustries(data);
    } catch (err) {
      console.error("Error fetching industries", err);
      setError("Failed to load industries.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (admin) {
      fetchIndustries();
    }
  }, [admin]);

  const sortedIndustries = useMemo(() => {
    return [...industries].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [industries]);

  function handleCreateFormChange(e) {
    const { name, value, type, checked } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        name: createForm.name.trim(),
        slug: createForm.slug.trim() || undefined,
        description: createForm.description.trim(),
        parent_industry: createForm.parent_industry.trim() || undefined,
        is_active: !!createForm.is_active,
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      // Clear form
      setCreateForm({
        name: "",
        slug: "",
        description: "",
        parent_industry: "",
        is_active: true,
      });

      // Refresh list
      fetchIndustries();
    } catch (err) {
      console.error("Error creating industry", err);
      setError(err.message || "Failed to create industry.");
    }
  }

  function startEditing(ind) {
    setEditingId(ind.id);
    setEditForm({
      name: ind.name || "",
      slug: ind.slug || "",
      description: ind.description || "",
      parent_industry: ind.parent_industry || "",
      is_active: ind.is_active !== false,
    });
  }

  function handleEditFormChange(e) {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editingId) return;
    setError("");

    try {
      const payload = {
        name: editForm.name.trim(),
        slug: editForm.slug.trim() || undefined,
        description: editForm.description.trim(),
        parent_industry: editForm.parent_industry.trim() || undefined,
        is_active: !!editForm.is_active,
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/industries/${editingId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      setEditingId(null);
      fetchIndustries();
    } catch (err) {
      console.error("Error updating industry", err);
      setError(err.message || "Failed to update industry.");
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Industries admin
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Create new industry */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Create new industry
              </Typography>
              <form onSubmit={handleCreate}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <label>
                    Name*
                    <input
                      type="text"
                      name="name"
                      value={createForm.name}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label>
                    Slug (optional)
                    <input
                      type="text"
                      name="slug"
                      value={createForm.slug}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label>
                    Parent industry (optional)
                    <input
                      type="text"
                      name="parent_industry"
                      value={createForm.parent_industry}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label style={{ gridColumn: "1 / -1" }}>
                    Description
                    <textarea
                      name="description"
                      value={createForm.description}
                      onChange={handleCreateFormChange}
                      rows={3}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label>
                    Active?
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={!!createForm.is_active}
                      onChange={handleCreateFormChange}
                      style={{ marginLeft: 8 }}
                    />
                  </label>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained">
                    Create industry
                  </Button>
                </Box>
              </form>
            </Paper>

            {/* List / edit industries */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing industries
              </Typography>

              {sortedIndustries.length === 0 ? (
                <Typography>No industries found.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Slug</TableCell>
                        <TableCell>Parent industry</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Active?</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedIndustries.map((ind) => (
                        <TableRow key={ind.id}>
                          <TableCell>{ind.name}</TableCell>
                          <TableCell>{ind.slug}</TableCell>
                          <TableCell>{ind.parent_industry || "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <span style={{ fontSize: "0.9rem" }}>{ind.description}</span>
                          </TableCell>
                          <TableCell>{ind.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {ind.created_at}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {ind.updated_at}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => startEditing(ind)}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Edit panel */}
            {editingId && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Edit industry
                </Typography>

                <form onSubmit={handleSaveEdit}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <label>
                      Name
                      <input
                        type="text"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Slug
                      <input
                        type="text"
                        name="slug"
                        value={editForm.slug}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Parent industry
                      <input
                        type="text"
                        name="parent_industry"
                        value={editForm.parent_industry}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label style={{ gridColumn: "1 / -1" }}>
                      Description
                      <textarea
                        name="description"
                        value={editForm.description}
                        onChange={handleEditFormChange}
                        rows={3}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Active?
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={!!editForm.is_active}
                        onChange={handleEditFormChange}
                        style={{ marginLeft: 8 }}
                      />
                    </label>
                  </Box>

                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Button type="submit" variant="contained">
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </form>
              </Paper>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default IndustriesAdmin;
